import { FC, useEffect, useState } from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { HighlightOff } from "@mui/icons-material";

import { Colors } from "../../../configs";
import { concat } from "lodash";

export interface ICustomModalProps {
  title: string;
  onClose?(): void;
  openModal: boolean;
  children?: React.ReactNode;
  width?: string;
  height?: string;
  mode?: "modal" | "popup";
  backdropProps?: any;
  disableBackdropClick?: boolean;
  enableBlurBackground?: boolean;
  titleSize?: string;
  titleColor?: string;
  hideCloseButton?: boolean;
  titleRight?: React.ReactNode;
  sx?: any;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  py: "3.49321429rem", // 3.99321429rem
  px: "3.57142857rem", // 5.57142857rem
  borderRadius: "1.75rem",
};

const CustomModal: FC<ICustomModalProps> = ({
  title,
  onClose,
  openModal,
  children,
  titleColor,
  titleSize,
  width = "30%",
  height = "40%",
  mode = "modal",
  titleRight,
  backdropProps = {
    sx: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  disableBackdropClick = false,
  enableBlurBackground = false,
  hideCloseButton = false,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown" | "closeButtonClick"
  ) => {
    if (
      !(
        disableBackdropClick &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      )
    ) {
      setOpen(false);
      onClose && onClose();
    }
  };

  useEffect(() => {
    setOpen(openModal);
  }, [openModal]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableScrollLock
      disableRestoreFocus
      sx={{
        zIndex: 999,
        ...(enableBlurBackground && {
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          },
        }),
      }}
      hideBackdrop={false}
      slotProps={{
        backdrop: {
          ...backdropProps,
          sx: {
            ...backdropProps.sx,
            ...(enableBlurBackground && {
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }),
          },
        },
      }}
    >
      <Box sx={{ ...style, width, height, ...sx }}>
        <Box
          sx={{
            display:
              mode === "popup" && title != "Customer Notes" ? "none" : "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "bold",
              color: titleColor ?? Colors.TEXT_GRAY_DARK,
              fontSize: concat(titleSize,"!") ?? "",
            }}
          >
            {title}
          </Typography>
          {titleRight}
          {!hideCloseButton && (
            <IconButton
              aria-label="close"
              test-name="close-button"
              onClick={() => handleClose({}, "closeButtonClick")}
              sx={{
                alignSelf: "flex-start",
                width: "2rem",
                height: "2rem",
                padding: "0",
              }}
            >
              <HighlightOff sx={{ fontSize: "2.5rem" }} />
            </IconButton>
          )}
        </Box>
        {children}
      </Box>
    </Modal>
  );
};

export default CustomModal;
